import type { PhotoPurpose } from './clinical.api';
import { api } from './client';
import { i18nArray } from '@/lib/i18n-map';

export interface UploadedDocument {
  id: string;
  name: string;
  path: string;
  size?: number;
}

/** Categories offered when filing a radiograph or a photo. */
export const IMAGE_CATEGORIES = i18nArray('common', [
  { key: 'radiografie', label: 'radiografie' },
  { key: 'panoramica', label: 'panoramica' },
  { key: 'intraoral', label: 'fotografieIntraorala' },
  { key: 'portret', label: 'fotografieDeFata' },
  { key: 'general', label: 'altele' },
]);

export const DOCUMENT_CATEGORIES = i18nArray('common', [
  { key: 'trimitere', label: 'trimitere' },
  { key: 'analize', label: 'analize' },
  { key: 'consimtamant', label: 'consimtamantSemnatPeHartie' },
  { key: 'factura', label: 'factura' },
  { key: 'general', label: 'altele' },
]);

/**
 * Uploads go through the same client as everything else, so a token that
 * expires mid-upload is refreshed and the request replayed rather than failing
 * in front of somebody holding a radiograph.
 */
export const filesApi = {
  uploadDocument: (patientId: string, file: File, category?: string) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<UploadedDocument>(`/files/patients/${patientId}/documents`, form, {
      query: { category },
    });
  },

  uploadImage: (patientId: string, file: File, category?: string, teeth?: string) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<UploadedDocument>(`/files/patients/${patientId}/images`, form, {
      query: { category, teeth },
    });
  },

  /**
   * Leagă o radiografie de dinții de pe ea, sau desface legătura.
   *
   * `null` șterge legătura fără să șteargă imaginea — filmul rămâne în dosar,
   * doar nu mai apare pe dinte.
   */
  setImageTeeth: (id: string, teeth: string | null) =>
    api.patch<{ id: string; teeth: string | null }>(`/files/images/${id}/teeth`, { teeth }),

  /** Acordul pacientului pentru o fotografie, cu scopurile (etapa 9, G8). */
  setPhotoConsent: (
    id: string,
    body: { consent: 'acordat' | 'refuzat' | null; purposes?: PhotoPurpose[] },
  ) =>
    api.patch<{
      id: string;
      photoConsent: 'acordat' | 'refuzat' | null;
      photoPurposes: PhotoPurpose[] | null;
    }>(`/files/images/${id}/consent`, body),

  /**
   * Sigla clinicii.
   *
   * Ruta există de la început și n-o chema nimeni: sigla se putea pune doar
   * editând configurația de tenant, adică de nimeni din clinică. Se păstrează
   * calea, nu imaginea — citirea trece printr-un link semnat, ca radiografiile.
   */
  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ path: string; url: string }>('/files/clinic/logo', form);
  },

  /** Semnătura desenată de pacient, pe un document emis pentru semnare. */
  signDocument: (patientId: string, docId: string, png: Blob, relatedPersonId?: string | null) => {
    const form = new FormData();
    form.append('file', png, 'signature.png');
    // Reprezentantul legal care semnează; fără el, semnează pacientul.
    if (relatedPersonId) form.append('relatedPersonId', relatedPersonId);
    return api.post<{ path: string; url: string }>(
      `/files/patients/${patientId}/documents-to-sign/${docId}/signature`,
      form,
    );
  },

  /** Semnătura mea, salvată în fișa mea de personal. */
  saveMySignature: (png: Blob) => {
    const form = new FormData();
    form.append('file', png, 'signature.png');
    return api.post<{ path: string; url: string }>('/files/me/signature', form);
  },

  mySignature: () => api.get<{ url: string | null }>('/files/me/signature'),

  /** Ștampila mea de medic, pe raportul radiologic tipărit. */
  saveMyStamp: (file: Blob) => {
    const form = new FormData();
    form.append('file', file, file instanceof File ? file.name : 'stamp.png');
    return api.post<{ url: string }>('/files/me/stamp', form);
  },

  myStamp: () => api.get<{ url: string | null }>('/files/me/stamp'),

  /** Ștampila clinicii, pe rapoartele tipărite. */
  uploadClinicStamp: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ url: string }>('/files/clinic/stamp', form);
  },

  clinicStamp: () => api.get<{ url: string | null }>('/files/clinic/stamp'),

  /**
   * A short-lived link to a stored file.
   *
   * Fetched on demand rather than kept in the list payload: these expire, and
   * a URL minted when the page loaded is dead by the time somebody clicks it
   * an hour later.
   */
  url: (path: string) =>
    api.get<{ url: string; expiresIn: number }>('/files/url', {
      query: { path },
    }),

  /**
   * Remove a file from the dossier — by row, not by storage path.
   *
   * The path-based route deleted the object and left the row behind, so the
   * document stayed on screen pointing at bytes that no longer existed. These
   * two soft-delete the row and keep the file, which is what lets Jurnal →
   * Șterse put it back.
   */
  removeDocument: (id: string, reason?: string) =>
    api.delete<{ ok: boolean }>(`/files/documents/${id}`, { query: { reason } }),

  removeImage: (id: string, reason?: string) =>
    api.delete<{ ok: boolean }>(`/files/images/${id}`, { query: { reason } }),
};
